'use client';

import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

interface CtaWidthContextValue {
  maxWidth: number;
  reportRef: (key: string, el: HTMLDivElement | null) => void;
  /** flip 가능한 일반 포지션이 함께 있는지(= 혼합 케이스). airdrop close 버튼 정렬 결정에 사용 */
  hasFlippablePosition: boolean;
}

export const CtaWidthContext = createContext<CtaWidthContextValue>({
  maxWidth: 0,
  reportRef: () => {},
  hasFlippablePosition: false,
});

export function useCtaWidthProvider() {
  const refsMap = useRef(new Map<string, HTMLDivElement>());
  const [maxWidth, setMaxWidth] = useState(0);
  const observerRef = useRef<ResizeObserver | null>(null);

  const recalculate = useCallback(() => {
    let max = 0;
    for (const el of refsMap.current.values()) {
      max = Math.max(max, el.scrollWidth);
    }
    setMaxWidth((prev) => (prev !== max ? max : prev));
  }, []);

  useEffect(() => {
    const observer = new ResizeObserver(recalculate);
    observerRef.current = observer;

    for (const el of refsMap.current.values()) {
      observer.observe(el);
    }

    return () => observer.disconnect();
  }, [recalculate]);

  const reportRef = useCallback(
    (key: string, el: HTMLDivElement | null) => {
      const observer = observerRef.current;
      if (el) {
        refsMap.current.set(key, el);
        observer?.observe(el);
      } else {
        const prev = refsMap.current.get(key);
        if (prev) observer?.unobserve(prev);
        refsMap.current.delete(key);
      }
      recalculate();
    },
    [recalculate],
  );

  return { maxWidth, reportRef };
}

export function useCtaWidth() {
  return useContext(CtaWidthContext);
}
